import React, { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

function NotificationHistory() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, emi_reminder, overdue_payment_reminder, etc.
  const [statusFilter, setStatusFilter] = useState('all'); // all, sent, failed
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    fetchNotificationHistory();
  }, [filter, statusFilter, dateFilter, searchTerm, currentPage, pageSize]);

  const fetchNotificationHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: pageSize,
        filter: filter,
        status: statusFilter,
        dateFilter: dateFilter,
        search: searchTerm
      });

      const response = await axios.get(`${config.API_URL}/notifications/history?${params}`);
      setNotifications(response.data.notifications || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (err) {
      console.error('Failed to load notification history:', err);
      setError('Failed to load notification history: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const formatNotificationType = (type) => {
    const typeMap = {
      'emi_reminder': 'EMI Reminder',
      'overdue_payment_reminder': 'Overdue Payment',
      'emi_payment_reminder': 'EMI Payment Reminder',
      'payment_confirmation': 'Payment Confirmation',
      'purchase_confirmation': 'Purchase Confirmation',
      'thank_you': 'Thank You',
      'reward_points': 'Reward Points',
      'test': 'Test Message',
      'witness_rewards': 'Witness Rewards',
      'selfwitness_rewards': 'Self Witness Rewards',
      'faq_auto_reply': 'FAQ Auto Reply',
      'incoming_message': 'Incoming Message'
    };
    return typeMap[type] || type.replace(/_/g, ' ').toUpperCase();
  };

  const getNotificationTypeBadgeColor = (type) => {
    const colorMap = {
      'emi_reminder': 'warning',
      'overdue_payment_reminder': 'danger',
      'emi_payment_reminder': 'info',
      'payment_confirmation': 'success',
      'purchase_confirmation': 'primary',
      'thank_you': 'success',
      'reward_points': 'info',
      'test': 'secondary',
      'witness_rewards': 'success',
      'selfwitness_rewards': 'success',
      'faq_auto_reply': 'info',
      'incoming_message': 'primary'
    };
    return colorMap[type] || 'secondary';
  };

  const getStatusBadgeColor = (status) => {
    const colorMap = {
      'sent': 'success',
      'failed': 'danger',
      'pending': 'warning'
    };
    return colorMap[status] || 'secondary';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('hi-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFilteredNotifications = () => {
    let filtered = notifications;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(notification =>
        notification.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.customerPhone?.includes(searchTerm) ||
        notification.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    fetchNotificationHistory();
  };

  const exportToCSV = () => {
    const filteredNotifications = getFilteredNotifications();
    
    if (filteredNotifications.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = [
      'Date & Time',
      'Customer Name',
      'Customer Phone',
      'Type',
      'Channel',
      'Status',
      'Message',
      'Provider',
      'Cost'
    ];

    const csvData = filteredNotifications.map(notification => [
      formatDate(notification.sentAt),
      notification.customerName || 'Unknown',
      notification.customerPhone || 'Unknown',
      formatNotificationType(notification.type),
      notification.channel?.toUpperCase() || 'Unknown',
      notification.status?.toUpperCase() || 'Unknown',
      `"${(notification.message || '').replace(/"/g, '""')}"`,
      notification.provider || 'Unknown',
      notification.cost || '₹0.00'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `notification_history_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && notifications.length === 0) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0 d-flex align-items-center">
          <i className="bi bi-bell me-2 fs-2"></i> Notification History
        </h2>
        <div>
          <button className="btn btn-outline-secondary me-2" onClick={handleRefresh}>
            <i className="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
          <button className="btn btn-success" onClick={exportToCSV}>
            <i className="bi bi-download me-1"></i> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <button className="btn btn-danger" onClick={handleRefresh}>Try Again</button>
        </div>
      )}

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Search</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by customer name, phone, or message..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="emi_reminder">EMI Reminders</option>
                <option value="overdue_payment_reminder">Overdue Payments</option>
                <option value="payment_confirmation">Payment Confirmations</option>
                <option value="purchase_confirmation">Purchase Confirmations</option>
                <option value="thank_you">Thank You Messages</option>
                <option value="reward_points">Reward Points</option>
                <option value="test">Test Messages</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Date Range</label>
              <select
                className="form-select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Page Size</label>
              <select
                className="form-select"
                value={pageSize}
                onChange={(e) => setPageSize(parseInt(e.target.value))}
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Notifications</h5>
              <h3>{notifications.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">Successfully Sent</h5>
              <h3>{notifications.filter(n => n.status === 'sent').length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h5 className="card-title">Failed</h5>
              <h3>{notifications.filter(n => n.status === 'failed').length}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">EMI Reminders</h5>
              <h3>{notifications.filter(n => n.type === 'emi_reminder' || n.type === 'overdue_payment_reminder').length}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Notifications Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date & Time</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th>Provider</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredNotifications().length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <p className="text-muted mb-0">No notifications found matching the selected filters.</p>
                    </td>
                  </tr>
                ) : (
                  getFilteredNotifications().map((notification, index) => (
                    <tr key={notification._id || index}>
                      <td>
                        <small>{formatDate(notification.sentAt)}</small>
                      </td>
                      <td>
                        <strong>{notification.customerName || 'Unknown'}</strong><br />
                        <small className="text-muted">{notification.customerPhone || 'Unknown'}</small>
                      </td>
                      <td>
                        <span className={`badge bg-${getNotificationTypeBadgeColor(notification.type)}`}>
                          {formatNotificationType(notification.type)}
                        </span>
                      </td>
                      <td>
                        {notification.channel === 'sms' ? (
                          <span className="badge bg-primary">SMS</span>
                        ) : notification.channel === 'whatsapp' ? (
                          <span className="badge bg-success">WhatsApp</span>
                        ) : (
                          <span className="badge bg-secondary">{notification.channel?.toUpperCase() || 'Unknown'}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge bg-${getStatusBadgeColor(notification.status)}`}>
                          {notification.status?.toUpperCase() || 'Unknown'}
                        </span>
                      </td>
                      <td style={{ maxWidth: '300px' }}>
                        <div className="text-truncate" title={notification.message}>
                          {notification.message || 'No message'}
                        </div>
                      </td>
                      <td>
                        <small>{notification.provider || 'Unknown'}</small>
                      </td>
                      <td>
                        <small>{notification.cost || '₹0.00'}</small>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div>
            <small className="text-muted">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, notifications.length)} of {notifications.length} notifications
            </small>
          </div>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <li key={page} className={`page-item ${page === currentPage ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                );
              })}
              
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

export default NotificationHistory;
